import { useMemo, useState } from 'react';
import { getOrgType, NEXT_ORG_SUGGESTION_ORDER } from '../../lib/orgTypes';

const STORAGE_KEY = 'precious_dismissed_org_suggestions';

const readDismissed = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

const article = (word) => (/^[aeiou]/i.test(word) ? 'an' : 'a');

/**
 * The next kind of organisation someone hasn't added yet, asked as a personal
 * question ("Were you registered with a GP surgery?") rather than a generic
 * "add another". "Not now" moves on to the next kind and is remembered per
 * browser; adding the kind retires it. Null until there is at least one
 * organisation, and once every kind has been added or dismissed.
 */
export const useNextOrgSuggestion = (organisations = []) => {
  const [dismissed, setDismissed] = useState(readDismissed);

  const suggestion = useMemo(() => {
    if (organisations.length === 0) return null;
    const having = new Set(organisations.map((o) => o.type));
    const value = NEXT_ORG_SUGGESTION_ORDER.find((t) => !having.has(t) && !dismissed.includes(t));
    if (!value) return null;
    const type = getOrgType(value);
    return {
      value,
      prompt: type.prompt,
      ctaLabel: `Add ${article(type.singular)} ${type.singular}`,
      ctaPath: `/organisations?action=add&type=${value}`,
    };
  }, [organisations, dismissed]);

  const dismiss = () => {
    if (!suggestion) return;
    const next = [...dismissed, suggestion.value];
    setDismissed(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  return { suggestion, dismiss };
};

export default useNextOrgSuggestion;
